// SVG geometry for the body map figure. Both anterior and posterior figures
// share the same shape skeleton; only the slug-per-shape mapping differs.
//
// Coordinate system: each figure renders inside a 200x600 box, midline at x=100.
// "viewerLeft" = the shape drawn at x < 100 (viewer's left side of the figure).
// For the anterior view, the figure faces the viewer, so viewer-left maps to
// the participant's anatomical right. For the posterior view, viewer-left maps
// to the participant's anatomical left.

import { VIEW_ANTERIOR, VIEW_POSTERIOR } from './constants';

// Arm rotation: each arm segment carries a `rotate: { deg, cx, cy }` describing
// an SVG rotate() about the shoulder pivot. Positive deg is clockwise (in the
// y-down SVG space), which swings the viewer-left arm outward to the left;
// negative deg swings the viewer-right arm outward to the right. ~10deg clears
// the torso/thighs without pushing the hands outside the 200-wide viewBox.
const ARM_PIVOT_LEFT = { deg: 10, cx: 58, cy: 116 };
const ARM_PIVOT_RIGHT = { deg: -10, cx: 142, cy: 116 };

// Each entry is one anatomical landmark with a fixed shape (or shape pair, for
// bilateral landmarks) and a slug map keyed by view + side (where applicable).
const SKELETON = [
  // Head
  {
    midline: { type: 'circle', cx: 100, cy: 50, r: 28 },
    slug: { [VIEW_ANTERIOR]: 'face', [VIEW_POSTERIOR]: 'back_head' },
  },
  // Neck
  {
    midline: { type: 'rect', x: 91, y: 78, w: 18, h: 18, rx: 4 },
    slug: { [VIEW_ANTERIOR]: 'front_neck', [VIEW_POSTERIOR]: 'back_neck' },
  },
  // Shoulders (bilateral)
  {
    viewerLeft: { type: 'circle', cx: 64, cy: 104, r: 11 },
    viewerRight: { type: 'circle', cx: 136, cy: 104, r: 11 },
    slug: {
      [VIEW_ANTERIOR]: { viewerLeft: 'front_right_shoulder', viewerRight: 'front_left_shoulder' },
      [VIEW_POSTERIOR]: { viewerLeft: 'back_left_shoulder', viewerRight: 'back_right_shoulder' },
    },
  },
  // Chest / Upper back
  {
    midline: { type: 'rect', x: 73, y: 100, w: 54, h: 60, rx: 6 },
    slug: { [VIEW_ANTERIOR]: 'chest', [VIEW_POSTERIOR]: 'upper_back' },
  },
  // Abdomen / Lower back
  {
    midline: { type: 'rect', x: 76, y: 162, w: 48, h: 50, rx: 6 },
    slug: { [VIEW_ANTERIOR]: 'abdomen', [VIEW_POSTERIOR]: 'lower_back' },
  },
  // Pelvis (anterior) — single trapezoid (no posterior equivalent at this slot)
  {
    midline: { type: 'polygon', points: '76,214 124,214 116,248 84,248' },
    slug: { [VIEW_ANTERIOR]: 'pelvis' },
  },
  // Buttocks (posterior) — bilateral pair at the same y range as pelvis
  {
    viewerLeft: { type: 'polygon', points: '78,214 100,214 100,248 84,248' },
    viewerRight: { type: 'polygon', points: '100,214 122,214 116,248 100,248' },
    slug: {
      [VIEW_POSTERIOR]: { viewerLeft: 'left_buttock', viewerRight: 'right_buttock' },
    },
  },
  // Hips (anterior only) — small circles at outer top of pelvis
  {
    viewerLeft: { type: 'circle', cx: 70, cy: 220, r: 9 },
    viewerRight: { type: 'circle', cx: 130, cy: 220, r: 9 },
    slug: {
      [VIEW_ANTERIOR]: { viewerLeft: 'right_hip', viewerRight: 'left_hip' },
    },
  },
  // Upper arms
  // Arms are angled ~10deg outward (rotated about the shoulder pivot) so the
  // forearms/hands clear the torso and thighs and stay easily clickable. Every
  // arm segment shares the same pivot per side (ARM_PIVOT_LEFT / _RIGHT) so the
  // whole limb swings as a rigid unit and the joints stay connected.
  {
    viewerLeft: { type: 'rect', x: 50, y: 116, w: 16, h: 70, rx: 7, rotate: ARM_PIVOT_LEFT },
    viewerRight: { type: 'rect', x: 134, y: 116, w: 16, h: 70, rx: 7, rotate: ARM_PIVOT_RIGHT },
    slug: {
      [VIEW_ANTERIOR]: { viewerLeft: 'front_right_upper_arm', viewerRight: 'front_left_upper_arm' },
      [VIEW_POSTERIOR]: { viewerLeft: 'back_left_upper_arm', viewerRight: 'back_right_upper_arm' },
    },
  },
  // Elbows
  {
    viewerLeft: { type: 'circle', cx: 58, cy: 192, r: 9, rotate: ARM_PIVOT_LEFT },
    viewerRight: { type: 'circle', cx: 142, cy: 192, r: 9, rotate: ARM_PIVOT_RIGHT },
    slug: {
      [VIEW_ANTERIOR]: { viewerLeft: 'front_right_elbow', viewerRight: 'front_left_elbow' },
      [VIEW_POSTERIOR]: { viewerLeft: 'back_left_elbow', viewerRight: 'back_right_elbow' },
    },
  },
  // Lower arms / forearms
  {
    viewerLeft: { type: 'rect', x: 50, y: 202, w: 15, h: 68, rx: 6, rotate: ARM_PIVOT_LEFT },
    viewerRight: { type: 'rect', x: 135, y: 202, w: 15, h: 68, rx: 6, rotate: ARM_PIVOT_RIGHT },
    slug: {
      [VIEW_ANTERIOR]: { viewerLeft: 'front_right_lower_arm', viewerRight: 'front_left_lower_arm' },
      [VIEW_POSTERIOR]: { viewerLeft: 'back_left_lower_arm', viewerRight: 'back_right_lower_arm' },
    },
  },
  // Wrists
  {
    viewerLeft: { type: 'circle', cx: 57, cy: 276, r: 7, rotate: ARM_PIVOT_LEFT },
    viewerRight: { type: 'circle', cx: 143, cy: 276, r: 7, rotate: ARM_PIVOT_RIGHT },
    slug: {
      [VIEW_ANTERIOR]: { viewerLeft: 'front_right_wrist', viewerRight: 'front_left_wrist' },
      [VIEW_POSTERIOR]: { viewerLeft: 'back_right_wrist', viewerRight: 'back_left_wrist' },
    },
  },
  // Hands
  {
    viewerLeft: { type: 'ellipse', cx: 57, cy: 300, rx: 10, ry: 14, rotate: ARM_PIVOT_LEFT },
    viewerRight: { type: 'ellipse', cx: 143, cy: 300, rx: 10, ry: 14, rotate: ARM_PIVOT_RIGHT },
    slug: {
      [VIEW_ANTERIOR]: { viewerLeft: 'front_right_hand', viewerRight: 'front_left_hand' },
      [VIEW_POSTERIOR]: { viewerLeft: 'back_right_hand', viewerRight: 'back_left_hand' },
    },
  },
  // Thighs
  // Legs are spread LEG_SPREAD px outward per side (symmetric about the x=100
  // midline) so the left/right leg segments don't touch — the feet previously
  // overlapped at the midline. Each leg stays vertical (only x/cx shifts), so
  // the clickable hit region still matches the drawn shape and the bigger gap
  // is easier to tap on small/touch screens.
  {
    viewerLeft: { type: 'rect', x: 74, y: 250, w: 20, h: 100, rx: 8 },
    viewerRight: { type: 'rect', x: 106, y: 250, w: 20, h: 100, rx: 8 },
    slug: {
      [VIEW_ANTERIOR]: { viewerLeft: 'front_right_thigh', viewerRight: 'front_left_thigh' },
      [VIEW_POSTERIOR]: { viewerLeft: 'back_left_thigh', viewerRight: 'back_right_thigh' },
    },
  },
  // Knees
  {
    viewerLeft: { type: 'circle', cx: 84, cy: 360, r: 10 },
    viewerRight: { type: 'circle', cx: 116, cy: 360, r: 10 },
    slug: {
      [VIEW_ANTERIOR]: { viewerLeft: 'front_right_knee', viewerRight: 'front_left_knee' },
      [VIEW_POSTERIOR]: { viewerLeft: 'back_left_knee', viewerRight: 'back_right_knee' },
    },
  },
  // Shins (anterior) / Calves (posterior)
  {
    viewerLeft: { type: 'rect', x: 76, y: 374, w: 17, h: 100, rx: 7 },
    viewerRight: { type: 'rect', x: 107, y: 374, w: 17, h: 100, rx: 7 },
    slug: {
      [VIEW_ANTERIOR]: { viewerLeft: 'front_right_shin', viewerRight: 'front_left_shin' },
      [VIEW_POSTERIOR]: { viewerLeft: 'back_left_calf', viewerRight: 'back_right_calf' },
    },
  },
  // Ankles
  {
    viewerLeft: { type: 'circle', cx: 84, cy: 484, r: 8 },
    viewerRight: { type: 'circle', cx: 116, cy: 484, r: 8 },
    slug: {
      [VIEW_ANTERIOR]: { viewerLeft: 'front_right_ankle', viewerRight: 'front_left_ankle' },
      [VIEW_POSTERIOR]: { viewerLeft: 'back_left_ankle', viewerRight: 'back_right_ankle' },
    },
  },
  // Feet
  {
    viewerLeft: { type: 'ellipse', cx: 84, cy: 510, rx: 13, ry: 18 },
    viewerRight: { type: 'ellipse', cx: 116, cy: 510, rx: 13, ry: 18 },
    slug: {
      [VIEW_ANTERIOR]: { viewerLeft: 'front_right_foot', viewerRight: 'front_left_foot' },
      [VIEW_POSTERIOR]: { viewerLeft: 'back_left_foot', viewerRight: 'back_right_foot' },
    },
  },
];

// Layer ordering note: torso/limb segments must render before joints so the
// joint circles sit on top visually. SKELETON is ordered head-down, joints
// interleaved; the renderer trusts this order.

export function buildFigureRegions(view, overrides = {}) {
  const regions = [];
  const apply = (slug, defaultShape) => {
    const override = overrides?.[slug];
    const shape = override ? { ...defaultShape, ...override } : defaultShape;
    regions.push({ slug, shape });
  };
  for (const entry of SKELETON) {
    const slugForView = entry.slug?.[view];
    if (!slugForView) continue;

    if (entry.midline) {
      apply(slugForView, entry.midline);
      continue;
    }

    if (entry.viewerLeft && slugForView.viewerLeft) {
      apply(slugForView.viewerLeft, entry.viewerLeft);
    }
    if (entry.viewerRight && slugForView.viewerRight) {
      apply(slugForView.viewerRight, entry.viewerRight);
    }
  }
  return regions;
}

// Default geometry lookup for a single slug — used by the editor to display
// canonical values alongside overrides and for "reset to default" behavior.
export function getDefaultShape(slug) {
  for (const entry of SKELETON) {
    const slugForView = entry.slug || {};
    for (const view of Object.keys(slugForView)) {
      const v = slugForView[view];
      if (entry.midline && v === slug) return entry.midline;
      if (entry.viewerLeft && v?.viewerLeft === slug) return entry.viewerLeft;
      if (entry.viewerRight && v?.viewerRight === slug) return entry.viewerRight;
    }
  }
  return null;
}

export const FIGURE_VIEWBOX = { width: 200, height: 600 };
